import { Observable, defer, of } from 'rxjs';
import { catchError, defaultIfEmpty, finalize, map, shareReplay, take } from 'rxjs/operators';

import { USER_MESSAGE_ENSURED_KEY_PREFIX } from 'common/sessionKeys';

/**
 * Explore asks the server to "create the rotate-landscape user message if it
 * doesn't exist yet" (CreateUserMessage is create-if-not-exists server-side).
 * Previously Home POSTed it on every Explore entry and casino change, in
 * parallel with MapStats' GetUserMessages — so on a first visit the GET could
 * run before the message existed.
 *
 * This helper makes the create:
 *  - at most once per user + casino per tab session (sessionStorage flag, set
 *    only after the server answered);
 *  - deduped while in flight, so Home and MapStats share one POST;
 *  - safe to chain before GetUserMessages: it always completes with a single
 *    `undefined` (errors are swallowed) so the GET is never blocked.
 */

type CreateUserMessageParams = {
  templateKey: string;
  title?: string;
  description?: string;
  isVisible?: boolean;
  casinoId?: number;
  appUserPk?: string;
};

export type UserMessageCreator = {
  CreateUserMessage: (params: CreateUserMessageParams) => Observable<unknown>;
};

export const ROTATE_LANDSCAPE_MESSAGE = {
  templateKey: 'rotate_landscape_message',
  title: 'Rotate to view in landscape',
  description: 'Note: You may need to enable screen rotation on your phone.',
  isVisible: true,
} as const;

const inflight = new Map<string, Observable<void>>();

export function userMessageEnsuredKey(
  appUserPk: string | null | undefined,
  casinoId: number | string
): string {
  return `${USER_MESSAGE_ENSURED_KEY_PREFIX}${String(appUserPk || 'anon')}::${String(casinoId)}`;
}

function isEnsured(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function markEnsured(key: string): void {
  try {
    sessionStorage.setItem(key, '1');
  } catch {
    /* sessionStorage unavailable — in-memory in-flight dedupe still applies */
  }
}

/**
 * Create the rotate-landscape message for this user + casino unless it was
 * already done in this tab session. Emits `undefined` once, then completes.
 */
export function ensureRotateLandscapeMessage(
  authSvc: UserMessageCreator,
  appUserPk: string | null | undefined,
  casinoId: number | null | undefined
): Observable<void> {
  if (!casinoId) return of(undefined);
  const key = userMessageEnsuredKey(appUserPk, casinoId);
  if (isEnsured(key)) return of(undefined);

  const existing = inflight.get(key);
  if (existing) return existing;

  const isCurrent = () => inflight.get(key) === shared$;
  const shared$: Observable<void> = defer(() =>
    authSvc.CreateUserMessage({
      ...ROTATE_LANDSCAPE_MESSAGE,
      casinoId,
      appUserPk: appUserPk || undefined,
    })
  ).pipe(
    take(1),
    defaultIfEmpty(null as unknown),
    map((resp) => {
      if (resp != null) markEnsured(key);
      return undefined;
    }),
    // Never block GetUserMessages on a failed create; next visit retries.
    catchError(() => of(undefined)),
    finalize(() => {
      if (isCurrent()) inflight.delete(key);
    }),
    shareReplay({ bufferSize: 1, refCount: false })
  );
  inflight.set(key, shared$);
  return shared$;
}

/** Test helper — forget in-flight creates (sessionStorage is left to the test). */
export function resetEnsureUserMessageForTests(): void {
  inflight.clear();
}
